import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { LayoutDashboard, Settings, Bell, Users, LogOut, AlertTriangle, ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";
import { LogoMark } from "../components/site/Logo";

const LINKS = [
  ["/admin", "Requests", LayoutDashboard, "admin-nav-requests"],
  ["/admin/notifications", "Notifications", Bell, "admin-notif-log-tab-btn"],
  ["/admin/settings", "Settings", Settings, "admin-settings-tab-btn"],
  ["/admin/team", "Team & Security", Users, "admin-team-tab-btn"],
];

const Warnings = () => {
  const [info, setInfo] = useState(null);
  const loc = useLocation();
  useEffect(() => {
    api.get("/admin/integrations").then((r) => setInfo(r.data)).catch(() => {});
  }, [loc.pathname]);
  if (!info) return null;
  const items = [];
  if (!info.phone_set) items.push("Business phone number coming soon — CALL NOW buttons are disabled until you add it in Settings.");
  if (!info.owner_email_set) items.push("Owner notification email is not set — new requests are saved but no email alert is sent.");
  if (!info.sms_provider_ready) items.push("SMS alerts are not configured (Twilio keys missing) — no text messages will be sent.");
  else if (!info.owner_sms_set) items.push("Owner SMS phone is not set — no text alerts will be sent to you.");
  if (!info.is_24_7_confirmed) items.push("24/7 availability is not confirmed — the website does not advertise 24/7 until you turn it on in Settings.");
  if (!items.length) return null;
  return (
    <div className="mb-6 rounded-xl border border-yellow-400/40 bg-yellow-400/5 p-4" data-testid="admin-config-warnings">
      <p className="flex items-center gap-2 text-sm font-bold uppercase text-yellow-300"><AlertTriangle className="h-4 w-4" /> Configuration needed</p>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-gray-300">
        {items.map((i) => <li key={i}>{i}</li>)}
      </ul>
    </div>
  );
};

export default function AdminLayout() {
  const { user, logout } = useAuth();
  return (
    <div className="min-h-screen bg-[#0A0A0C] md:flex">
      <aside className="md:w-64 md:min-h-screen border-b md:border-b-0 md:border-r border-[#2E2E3A] bg-[#0E0E12]">
        <div className="flex items-center gap-3 p-4 md:p-6">
          <LogoMark className="h-9 w-9" />
          <div className="leading-tight">
            <p className="font-display text-lg font-extrabold uppercase">Dispatch</p>
            <p className="text-xs text-gray-500 truncate max-w-[150px]">{user?.email}</p>
          </div>
        </div>
        <nav className="flex md:flex-col gap-1 overflow-x-auto px-2 pb-2 md:px-3" aria-label="Admin">
          {LINKS.map(([to, label, Icon, tid]) => (
            <NavLink key={to} to={to} end={to === "/admin"} data-testid={tid}
              className={({ isActive }) => `flex shrink-0 items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors ${isActive ? "bg-[#DC2626] text-white" : "text-gray-300 hover:bg-white/5"}`}>
              <Icon className="h-4 w-4" /> {label}
            </NavLink>
          ))}
          <Link to="/" target="_blank" className="flex shrink-0 items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-gray-400 hover:bg-white/5" data-testid="admin-view-site-link"><ExternalLink className="h-4 w-4" /> View website</Link>
          <button type="button" onClick={logout} className="flex shrink-0 items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-gray-400 hover:bg-white/5" data-testid="admin-logout-btn"><LogOut className="h-4 w-4" /> Log out</button>
        </nav>
      </aside>
      <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-10">
        <Warnings />
        <Outlet />
      </main>
    </div>
  );
}
